import { ref, computed, type Ref } from 'vue'
import type { Request, ActivePhase, EstimationMode, PimMode } from '@/lib/dd-queue/types'
import type { ResolvedSubject } from '@/lib/dd-queue/resolveSubject'
import type { ChangeIntent, ChangeIntentKey } from '@/lib/dd-queue/types/subjectMutation'
import { changeIntentsToWire, type ChangeIntentForm } from '@/lib/dd-queue/changeIntents'
import { DELIVERABLE_PHASE_KEYS, STEP_LIBRARY } from '@/lib/dd-queue/data/steps'
import { requiredStepsForChanges } from '@/lib/dd-queue/modify'
import { isWalmartMarketplace } from '@/lib/dd-queue/locationRules'
import {
  draftsToParts,
  partsSupplierGaps,
  supplierGapsText,
  type LinePart,
  type LinePartDraft,
  type PartFieldIssue,
  type PartSupplierGap,
} from '@/lib/dd-queue/lineParts'
import {
  forceNpTriageLockedSelected,
  isNpTriageLockedKey,
  isNpTriageSoleSelectedPairMember,
  npTriagePairHintForMap,
  npTriagePairViolated,
  wouldViolateNpTriagePair,
} from '@/lib/dd-queue/npTriageComposition'

export const PMA_PHASE_LABELS: Record<string, string> = {
  intake: 'Intake',
  design_est: 'Design & Estimation',
  product_review: 'Product Review',
  approval: 'Sales Review',
  system: 'System',
  deliverables: 'Deliverables',
  closeout: 'Close-out',
}

export const PMA_PHASE_ORDER: ActivePhase[] = [
  'design_est',
  'product_review',
  'approval',
  'system',
  'deliverables',
  'closeout',
]

const ONE_OFF_DISABLE = [
  'sku',
  'uin',
  'signagent_svg',
  'web_image',
  'piq_quickpart',
  'pim_publish',
  'web_publish',
  'product_review',
] as const

const ONE_OFF_ENABLE = [
  'design',
  'estimation',
  'sales_approval',
  'production_file',
] as const

/**
 * Fresh New Product Triage — what the Deliverables group starts with:
 * - DD-TRIAGE-DEFAULTS-001 (F-083 Candace Loran): only Pre-press and Web Image ticked; Vendor
 *   File, Instructions, Assembled View, Install Notes and Hardware stay available but unticked
 *   (D&D was unticking them most of the time).
 * - DD-SVG-DEFAULT-001 (F-027 Jessica Padilla): SignAgent SVG ticked only for Walmart / Sam's
 *   Club — the SVG is produced for every product of that marketplace and for no other customer.
 * "Fresh" = Triage not yet completed and no key of the group carries a decision yet (all still
 * `pending`); once anything was decided, or Triage was completed, the form shows what is stored.
 * The other groups (System, Close-out) keep the intake default; every box stays available.
 */
export const SVG_DELIVERABLE_KEY = 'signagent_svg'

/** F-083 — Deliverables-group keys that start unticked on a fresh New Product Triage. */
export const NP_TRIAGE_DEFAULT_UNTICKED: readonly string[] = [
  'outsource_file',
  'instructions',
  'assembled_view',
  'install_notes',
  'hardware',
]

function isFreshDeliverablesGroup(resolved: ResolvedSubject): boolean {
  if (resolved.deliverables.pm_assessment?.status === 'complete') return false
  return DELIVERABLE_PHASE_KEYS.every((key) => {
    const status = resolved.deliverables[key]?.status
    return status === undefined || status === 'pending'
  })
}

function deliverableDefaultSelected(
  key: string,
  customer: string | null | undefined,
  persistedSelected: boolean,
  fresh: boolean
): boolean {
  if (!fresh) return persistedSelected
  if (key === SVG_DELIVERABLE_KEY) return isWalmartMarketplace(customer)
  if (NP_TRIAGE_DEFAULT_UNTICKED.includes(key)) return false
  return persistedSelected
}

/** NP-TRIAGE-PERSIST-001 — New Product (not bundle / modify) uses locked composition UI. */
export function isNpTriageCompositionScope(req: Request, resolved: ResolvedSubject): boolean {
  if (resolved.modify) return false
  if (req.path !== 'new') return false
  if (req.isMaster ? req.isBundle : false) return false
  if (!req.isMaster && 'isBundle' in req && (req as { isBundle?: boolean }).isBundle) {
    return false
  }
  return true
}

function buildInitialDeliverableMap(
  resolved: ResolvedSubject,
  npComposition: boolean,
  customer: string | null | undefined
): Record<string, boolean> {
  const isModify = !!resolved.modify
  const map: Record<string, boolean> = {}

  if (isModify && resolved.modify) {
    const required = requiredStepsForChanges(resolved.modify.changeTypes)
    // F-145 — a line sent back to Triage re-opens on the set it had (the server keeps every
    // step it decided; only the untouched first Triage starts from the change-type defaults).
    const fresh = isFreshDeliverablesGroup(resolved)
    for (const step of STEP_LIBRARY) {
      if (step.key === 'pm_assessment') continue
      map[step.key] = fresh
        ? required.has(step.key)
        : resolved.deliverables[step.key]?.status !== 'not_required'
    }
    return map
  }

  const fresh = npComposition && isFreshDeliverablesGroup(resolved)
  for (const step of STEP_LIBRARY) {
    if (step.key === 'pm_assessment') continue
    if (npComposition) {
      // NP-TRIAGE-CR-001 / PAIR-FIX-001 — free keys follow persisted SoT (fresh Triage: the
      // F-083 / F-027 defaults above); locked-required forced after. Pair violation is
      // surfaced, not flipped.
      const status = resolved.deliverables[step.key]?.status
      map[step.key] = deliverableDefaultSelected(
        step.key,
        customer,
        status !== 'not_required',
        fresh
      )
    } else {
      map[step.key] = true
    }
  }
  return npComposition ? forceNpTriageLockedSelected(map) : map
}

export interface PmaTriageState {
  deliverableMap: Ref<Record<string, boolean>>
  estimationMode: Ref<EstimationMode>
  pimMode: Ref<PimMode>
  installRequired: Ref<boolean>
  attrValues: Ref<Record<string, string>>
  /** Intake fill snapshot at form render — sent with Complete Triage for dirty detection. */
  attrBaseline: Ref<Record<string, string>>
  /**
   * F-099 — the parts editor rows. The Attributes card seeds them on first paint (stored list,
   * else the Sales spec) and sets `partsReady`; a kept draft (F-053 line switch) is reused as is.
   */
  partDrafts: Ref<LinePartDraft[]>
  partsReady: Ref<boolean>
  /** Set by the editor's first change — a line rebuild never re-seeds over the user's edits. */
  partsTouched: Ref<boolean>
  partsSeededFromIntake: Ref<boolean>
  /** The wire list Complete Triage sends (null until the card seeded the drafts). */
  parts: Ref<LinePart[] | null>
  /** What the API would refuse in the drafts (numbers, missing modifier inputs). */
  partsIssues: Ref<string[]>
  /** The same, pinned to inputs — the editor's inline marks (owner, staging 2026-10-07). */
  partsFieldIssues: Ref<PartFieldIssue[]>
  /** Complete Triage supplier gate (Candace): hardware / vended parts with empty required boxes. */
  partsGaps: Ref<PartSupplierGap[]>
  /** One sentence for the Complete Triage block, or null when the parts are fine. */
  partsBlock: Ref<string | null>
  /**
   * F-144 (slice 3a) — "What's changing" (update requests): the six boxes + notes. The card
   * seeds it on first paint (stored list, else pre-filled from the request); null until then.
   * A kept draft (F-053 line switch) is reused as is, like the attributes.
   */
  changeIntents: Ref<ChangeIntentForm | null>
  /** The wire list Complete Triage sends: marked boxes only; null = form not seeded / not an update. */
  changeIntentsWire: Ref<ChangeIntent[] | null>
  isModify: boolean
  /** NP locked composition (no soft enable-back gate). */
  npComposition: boolean
  pairHint: Ref<string | null>
  pairViolated: Ref<boolean>
  missing: Ref<string[]>
  canComplete: Ref<boolean>
  isDeliverableLocked: (key: string) => boolean
  toggleDeliverable: (key: string, value: boolean) => void
  setPimMode: (mode: PimMode) => void
  setAttrValue: (key: string, value: string) => void
  setChangeIntent: (key: ChangeIntentKey, patch: Partial<{ on: boolean; note: string }>) => void
}

export function createPmaTriageState(req: Request, resolved: ResolvedSubject): PmaTriageState {
  const isModify = !!resolved.modify
  const npComposition = isNpTriageCompositionScope(req, resolved)
  const deliverableMap = ref(buildInitialDeliverableMap(resolved, npComposition, req.customer))
  const estimationMode = ref(resolved.estimationMode)
  const pimMode = ref(resolved.pimMode)
  const installRequired = ref(resolved.installRequired)
  const attrValues = ref<Record<string, string>>({})
  const attrBaseline = ref<Record<string, string>>({})
  const partDrafts = ref<LinePartDraft[]>([])
  const partsReady = ref(false)
  const partsTouched = ref(false)
  const partsSeededFromIntake = ref(false)
  const partsResult = computed(() => draftsToParts(partDrafts.value))
  const parts = computed<LinePart[] | null>(() => (partsReady.value ? partsResult.value.parts : null))
  const partsIssues = computed(() => (partsReady.value ? partsResult.value.issues : []))
  const partsFieldIssues = computed(() => (partsReady.value ? partsResult.value.fieldIssues : []))
  const partsGaps = computed(() => (partsReady.value ? partsSupplierGaps(partsResult.value.parts) : []))
  const partsBlock = computed<string | null>(() => {
    if (partsIssues.value.length > 0) return `Fix the parts before completing Triage — ${partsIssues.value[0]}`
    if (partsGaps.value.length > 0) {
      return `Fill the Supplier info before completing Triage (type N/A when there is nothing to enter) — ${supplierGapsText(partsGaps.value)}.`
    }
    return null
  })

  const changeIntents = ref<ChangeIntentForm | null>(null)
  const changeIntentsWire = computed<ChangeIntent[] | null>(() =>
    isModify && changeIntents.value ? changeIntentsToWire(changeIntents.value) : null
  )

  const pimDefaults = buildInitialDeliverableMap(resolved, npComposition, req.customer)

  const pairHint = computed(() =>
    npComposition ? npTriagePairHintForMap(deliverableMap.value) : null
  )
  const pairViolated = computed(() =>
    npComposition && npTriagePairViolated(deliverableMap.value)
  )

  function isDeliverableLocked(key: string): boolean {
    if (!npComposition) return false
    if (isNpTriageLockedKey(key)) return true
    return isNpTriageSoleSelectedPairMember(deliverableMap.value, key)
  }

  function toggleDeliverable(key: string, value: boolean) {
    if (npComposition && isNpTriageLockedKey(key) && !value) {
      return
    }
    if (npComposition && wouldViolateNpTriagePair(deliverableMap.value, key, value)) {
      return
    }
    deliverableMap.value = { ...deliverableMap.value, [key]: value }
  }

  function setPimMode(mode: PimMode) {
    pimMode.value = mode
    if (mode === 'oneoff') {
      const next = { ...deliverableMap.value }
      for (const k of ONE_OFF_DISABLE) next[k] = false
      for (const k of ONE_OFF_ENABLE) next[k] = true
      deliverableMap.value = next
    } else {
      deliverableMap.value = { ...pimDefaults }
    }
  }

  function setAttrValue(key: string, value: string) {
    attrValues.value = { ...attrValues.value, [key]: value }
  }

  function setChangeIntent(key: ChangeIntentKey, patch: Partial<{ on: boolean; note: string }>) {
    const form = changeIntents.value
    if (!form) return
    changeIntents.value = { ...form, [key]: { ...form[key], ...patch } }
  }

  // Soft enable-back gate retained for non-NP / modify only.
  const missing = computed(() => {
    const out: string[] = []
    if (npComposition) return out
    if (!isModify) {
      if (!deliverableMap.value.design) out.push('Design')
      if (!deliverableMap.value.estimation) out.push('Estimation')
      if (!deliverableMap.value.sales_approval) out.push('Sales Review')
    }
    return out
  })

  const canComplete = computed(() => {
    // F-099 / F-050 — the parts list must be well-formed and every hardware / vended part must
    // carry its supplier info (the API refuses Complete Triage otherwise — same rule here).
    if (partsBlock.value !== null) return false
    if (npComposition) return true
    return isModify
      ? Object.values(deliverableMap.value).some(Boolean)
      : missing.value.length === 0
  })

  return {
    deliverableMap,
    estimationMode,
    pimMode,
    installRequired,
    attrValues,
    attrBaseline,
    partDrafts,
    partsReady,
    partsTouched,
    partsSeededFromIntake,
    parts,
    partsIssues,
    partsFieldIssues,
    partsGaps,
    partsBlock,
    changeIntents,
    changeIntentsWire,
    isModify,
    npComposition,
    pairHint,
    pairViolated,
    missing,
    canComplete,
    isDeliverableLocked,
    toggleDeliverable,
    setPimMode,
    setAttrValue,
    setChangeIntent,
  }
}
